// Use Shopify's versioned data URL so updated measurements cannot reuse an old cache entry.
const dataUrl = document.querySelector('script[data-size-guide-data]')?.dataset.sizeGuideData;
const { sizeProfiles, chartAssignments } = await import(dataUrl || './henry-size-guide-data.js');

// Exact size aliases avoid Karnevil's substring matches (for example S inside XS).
export function normalizeSize(value) {
  const text = String(value ?? '').trim().toLowerCase().replace(/[–—]/g, '-').replace(/\s*-\s*/g, '-');
  const aliases = {
    'extra small': 'xs', 'x-small': 'xs', 'x small': 'xs', small: 's', medium: 'm', large: 'l',
    'extra large': 'xl', 'x-large': 'xl', 'x large': 'xl', 'xx-large': '2xl', xxl: '2xl',
    'xxx-large': '3xl', xxxl: '3xl', 'xxxx-large': '4xl', xxxxl: '4xl',
    // Existing catalogue labels affected by spreadsheet date formatting.
    '06-aug': '6-8', '08-oct': '8-10', '10-dec': '10-12', 'dec-14': '12-14',
  };
  return aliases[text] || text.replace(/^uk\s*/, '').replace(/\byears?\b/g, 'y').replace(/\bmonths?\b/g, 'm').replace(/\s+/g, '').replace(/\b0+(?=\d)/g, '');
}

export function profileFor(product) {
  const chart = (product.chart || '').toLowerCase();
  const tags = product.tags || [];
  const option = (product.optionName || '').toLowerCase();
  const age = (product.age || '').toLowerCase();
  const gender = (product.gender || '').toLowerCase();
  if (chart.includes('smiffys')) {
    if (option.includes('baby') || age === 'baby' || age === 'infant') return 'karnevil_baby';
    if (option.includes('child') || /child|toddler|kid/.test(chart)) return 'karnevil_kids';
    if (option.includes('women') || chart.includes('women') || /^(female|women)$/.test(gender)) return 'karnevil_womens';
    if (option.includes('men') || chart.includes('men') || /^(male|men)$/.test(gender)) return 'karnevil_mens';
  }
  // Preserve the existing app's category assignments without contacting its servers.
  for (const [tag, profile] of [['WOMENS-SIZES', 'existing_womens'], ['MENS-SIZES', 'existing_mens'], ['CHILDRENS-SIZES', 'existing_kids'], ['BABY-SIZES', 'existing_baby']]) {
    if (tags.includes(tag)) return profile;
  }
  return chartAssignments[product.chart] || '';
}

export function measurementFor(profile, label) {
  const key = normalizeSize(label);
  return (sizeProfiles[profile] || []).find(item => normalizeSize(item.size) === key || item.rows.some(row => row.label === 'UK' && normalizeSize(row.value) === key));
}

class HenrySizeGuide extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    const data = this.querySelector('[data-size-guide-product]');
    if (!data) return;
    this.product = JSON.parse(data.textContent);
    this.profile = profileFor(this.product);
    if (!sizeProfiles[this.profile]?.length) return;
    this.ready = true;
    this.unit = 'inches';
    this.trigger = this.querySelector('[data-size-guide-open]');
    this.dialog = this.querySelector('dialog');
    this.tabs = [];
    this.panels = [];
    this.build();
    const baby = this.profile.endsWith('_baby');
    this.querySelector('[data-size-tips-general]').hidden = baby;
    this.querySelector('[data-size-tips-baby]').hidden = !baby;
    this.trigger.hidden = false;
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.dialog.addEventListener('close', this.onClose);
  }

  // Variant changes morph the children without replacing this custom element.
  // Rebind the dialog and tabs so opening the guide uses the current markup.
  updatedCallback() {
    this.disconnectedCallback();
    this.connectedCallback();
  }

  disconnectedCallback() {
    this.releaseScroll();
    const reference = this.querySelector('[data-size-reference]');
    if (reference) reference.ontoggle = null;
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.dialog?.removeEventListener('close', this.onClose);
    this.ready = false;
  }

  build() {
    const tabList = this.querySelector('[data-size-tabs]');
    const panelList = this.querySelector('[data-size-panels]');
    tabList.replaceChildren();
    panelList.replaceChildren();
    this.querySelector('[data-size-reference-table]').replaceChildren();
    tabList.hidden = !this.product.sizes.length;
    this.product.sizes.forEach((size, index) => {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'size-tab';
      tab.role = 'tab';
      tab.dataset.sizeIndex = index;
      tab.id = `${this.product.id}-tab-${index}`;
      tab.setAttribute('aria-controls', `${this.product.id}-panel-${index}`);
      tab.textContent = size;
      const panel = document.createElement('div');
      panel.id = `${this.product.id}-panel-${index}`;
      panel.className = 'size-content';
      panel.role = 'tabpanel';
      panel.tabIndex = 0;
      panel.setAttribute('aria-labelledby', tab.id);
      const measurements = measurementFor(this.profile, size);
      if (measurements) {
        const table = document.createElement('dl');
        table.className = 'size-measurements';
        measurements.rows.forEach(row => {
          const line = document.createElement('div');
          line.className = 'measurement-row';
          const label = document.createElement('dt');
          label.className = 'measurement-label';
          label.textContent = row.label;
          line.append(label);
          this.appendMeasurement(line, row, 'dd');
          table.append(line);
        });
        panel.append(table);
      } else {
        panel.append(this.missingNote(this.product.labels.missingSize));
      }
      tabList.append(tab);
      panelList.append(panel);
      this.tabs.push(tab);
      this.panels.push(panel);
    });
    if (!this.product.sizes.length) panelList.append(this.missingNote(this.product.labels.noSize));
    const reference = this.querySelector('[data-size-reference]');
    reference.open = !this.product.sizes.length;
    // Render the full chart only on request, keeping product-page startup light.
    reference.ontoggle = () => { if (reference.open) this.buildReference(); };
    if (reference.open) this.buildReference();
    this.select(Math.max(0, this.product.sizes.indexOf(this.product.selected)));
  }

  missingNote(message) {
    const note = document.createElement('p');
    note.className = 'size-measurements';
    note.textContent = `${message} `;
    const link = document.createElement('a');
    link.href = '/pages/contact';
    link.textContent = this.product.labels.contact;
    note.append(link, '.');
    return note;
  }

  appendMeasurement(parent, row, tag = 'span') {
    for (const unit of row.value !== undefined ? ['value'] : ['inches', 'cm']) {
      const value = document.createElement(tag);
      value.className = 'measurement-value';
      value.textContent = row[unit] === 'Not available' ? this.product.labels.unavailable : row[unit];
      if (unit !== 'value') {
        value.dataset.measurementUnit = unit;
        value.hidden = unit !== this.unit;
      }
      parent.append(value);
    }
  }

  buildReference() {
    const table = this.querySelector('[data-size-reference-table]');
    if (table.childElementCount) return;
    const sizes = sizeProfiles[this.profile];
    const columns = [...new Set(sizes.flatMap(size => size.rows.map(row => row.label)))];
    const head = document.createElement('thead');
    const headings = document.createElement('tr');
    for (const label of [this.product.labels.size, ...columns]) {
      const cell = document.createElement('th');
      cell.scope = 'col';
      cell.textContent = label;
      headings.append(cell);
    }
    head.append(headings);
    const body = document.createElement('tbody');
    for (const size of sizes) {
      const line = document.createElement('tr');
      const label = document.createElement('th');
      label.scope = 'row';
      label.textContent = size.size;
      line.append(label);
      for (const column of columns) {
        const cell = document.createElement('td');
        const row = size.rows.find(item => item.label === column);
        this.appendMeasurement(cell, row || { value: 'Not available' });
        line.append(cell);
      }
      body.append(line);
    }
    table.append(head, body);
  }

  select(index, focus = false) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.classList.toggle('active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      this.panels[i].hidden = !active;
    });
    this.activeIndex = index;
    if (focus) this.tabs[index].focus();
  }

  open() {
    const section = this.closest('.shopify-section');
    const picker = section?.querySelector('variant-picker');
    const checked = Array.from(picker?.querySelectorAll('input:checked, option:checked') || []).find(input => input.dataset.optionName === this.product.optionName);
    const checkedIndex = this.product.sizes.indexOf(checked?.value);
    if (checkedIndex >= 0) this.select(checkedIndex);
    const selected = picker?.querySelector('[data-selected-variant]');
    if (selected) {
      try {
        const variant = JSON.parse(selected.textContent);
        const label = variant.options?.[this.product.optionPosition - 1] ?? variant[`option${this.product.optionPosition}`];
        const index = this.product.sizes.indexOf(label);
        if (index >= 0) this.select(index);
      } catch { /* Retain the last selected size if the picker is updating. */ }
    }
    if (this.dialog.open) return;
    this.previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    this.scrollLocked = true;
    this.dialog.showModal();
  }

  releaseScroll() {
    if (!this.scrollLocked) return;
    document.documentElement.style.overflow = this.previousOverflow;
    this.scrollLocked = false;
  }

  onClose = () => {
    this.releaseScroll();
    if (this.isConnected) this.trigger.focus({ preventScroll: true });
  };

  onClick = event => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-size-guide-open]')) return this.open();
    if (event.target.closest('[data-size-guide-close]')) return this.dialog.close();
    if (event.target === this.dialog) {
      const rect = this.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      return;
    }
    const tab = event.target.closest('[data-size-index]');
    if (tab) return this.select(Number(tab.dataset.sizeIndex));
    const unit = event.target.closest('[data-unit]');
    if (unit) {
      this.unit = unit.dataset.unit;
      this.querySelectorAll('[data-unit]').forEach(button => {
        const active = button === unit;
        button.classList.toggle('active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      this.querySelectorAll('[data-measurement-unit]').forEach(value => { value.hidden = value.dataset.measurementUnit !== this.unit; });
      return;
    }
    const tips = event.target.closest('[data-size-tips]');
    if (tips) {
      const expanded = tips.getAttribute('aria-expanded') !== 'true';
      tips.setAttribute('aria-expanded', String(expanded));
      this.querySelector('.measurement-guide-container').hidden = !expanded;
    }
  };

  onKeydown = event => {
    if (!event.target.matches('[role="tab"]')) return;
    let index = Number(event.target.dataset.sizeIndex);
    if (event.key === 'ArrowRight') index = (index + 1) % this.tabs.length;
    else if (event.key === 'ArrowLeft') index = (index - 1 + this.tabs.length) % this.tabs.length;
    else if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = this.tabs.length - 1;
    else return;
    event.preventDefault();
    this.select(index, true);
  };
}
if (!customElements.get('henry-size-guide')) customElements.define('henry-size-guide', HenrySizeGuide);
